import React from 'react';
import { ProductViewModel } from '~/lib/data-contract';
import { useTranslation } from '~/shared/utils';
import { StyledProductActions } from './styled';
import ShoppingCart from '$icons/solidAsh/shopping-cart.svg';
import { Button } from '~/shared/components';
import { useStoresWithStock } from '~/features/stores';
import { ReserveStockCount } from '~/features/Reserve';
import { useModuleFeature } from '~/templates/blocks/hooks';
import { EasysizeElementIds } from '../Easysize/easysize-settings';
import { useAccount } from '$features/club';

export type ProductActionsProps = Pick<ProductViewModel, 'sizes' | 'productColorId'> & {
    activeSizeId?: string | null;
    onAddToBasket: () => void;
    onReserve: () => void;
};

export const ProductActions = ({
    sizes,
    activeSizeId,
    productColorId,
    onAddToBasket,
    onReserve,
}: ProductActionsProps) => {
    const { reserveInStore } = useModuleFeature();
    const { translate } = useTranslation();
    const selectedSize = sizes?.find((size) => size.variantId === activeSizeId);
    const { inStockOnWeb, inStockInStore } = selectedSize || {};
    const availableOnWeb = selectedSize ? inStockOnWeb : sizes?.some((size) => size.inStockOnWeb);
    const availableInStore = selectedSize
        ? inStockInStore
        : sizes?.some((size) => size.inStockInStore);
    const { account } = useAccount();
    const {
        data: { stores },
    } = useStoresWithStock({
        searchParams: {
            Radius: 10_000_000,
            ZipCode: '0',
        },
        searchTerm: '',
        slugs: {
            productColorId,
            variantId: activeSizeId || undefined,
        },
    });

    return (
        <StyledProductActions>
            {reserveInStore ? (
                <>
                    {/* We do not show out of stock products, so 'disabled' shouldn't happen, which is why we have no translation for it either */}
                    <Button
                        id={EasysizeElementIds.add_to_cart_btn_selector}
                        onClick={onAddToBasket}
                        disabled={!availableOnWeb}
                    >
                        <ShoppingCart />
                        {translate('general.addToBasket')}
                    </Button>
                    {/* Button was initially disabled if no store stock, but now it's completely hidden. Translation remains if reverting */}
                    {availableInStore && (
                        <Button variant="secondary" onClick={onReserve}>
                            {translate(
                                `reservation.button.${
                                    availableInStore ? 'initiateFlow' : 'notInStock'
                                }`
                            )}
                        </Button>
                    )}
                    <ReserveStockCount
                        isProductAction
                        stores={inStockInStore ? stores : undefined}
                        account={inStockInStore ? account : undefined}
                        openReserveModal={onReserve}
                    />
                </>
            ) : (
                <Button
                    id={EasysizeElementIds.add_to_cart_btn_selector}
                    onClick={onAddToBasket}
                    children={translate('general.addToBasket')}
                />
            )}
        </StyledProductActions>
    );
};
